import { useCallback, useEffect, useState } from 'react'
import { ASTNode } from 'graphql'
import { isEqual } from 'lodash'
import graphql, { QueryResponse } from 'utils/graphql'
import { Pagination } from 'generated/graphql'
import { RequestStatus } from 'types'
import { usePrevious } from '.'

const useQuery = <TQuery = any, TVariables = any> (
    query: ASTNode,
    variables?: TVariables,
    getPageInfo?: (data) => Pagination,
    preventFetching?: boolean,
) => {
    const [response, setResponse] = useState<QueryResponse<TQuery>>(null)
    const [requestStatus, setRequestStatus] = useState<RequestStatus>('idle')

    const fetchData = useCallback(async () => {
        if (preventFetching) {
            return
        }
        setRequestStatus('loading')
        try {
            const _response = await graphql.createQuery<TQuery, TVariables>(query)(variables)
            setResponse(_response)
            setRequestStatus('succeeded')
        } catch (err) {
            setRequestStatus('failed')
        }
    }, [preventFetching, query, variables])

    const previousVariables = usePrevious(variables)
    useEffect(() => {
        if (!isEqual(variables, previousVariables)) {
            fetchData()
        }
    }, [variables, fetchData, previousVariables])

    return {
        data: response?.data,
        pageInfo: getPageInfo?.(response?.data),
        requestStatus,
        fetchData
    }
}

export default useQuery
